import React, { useState } from 'react';
import { TodoTree, TodoItem } from '../types/todo';
import { getItemPath } from '../utils/tree';
import { getPriorityColor, getPriorityIcon } from '../utils/priority';

interface HighlightedTasksProps {
  tree: TodoTree;
}

interface HighlightedTask {
  id: string;
  item: TodoItem;
  pathNames: string[];
}

/**
 * Get all highlighted tasks from the tree
 * Returns tasks that are marked as highlighted and not archived
 */
const getHighlightedTasks = (tree: TodoTree): HighlightedTask[] => {
  const highlightedTasks: HighlightedTask[] = [];

  for (const [id, item] of Object.entries(tree)) {
    // Include highlighted tasks that are not archived
    if (item.highlighted && !item.archived) {
      const path = getItemPath(tree, id);
      if (path) {
        highlightedTasks.push({
          id,
          item,
          pathNames: path.map(pathId => tree[pathId].name),
        });
      }
    }
  }

  // Sort by priority (critical > high > medium > low), then by name
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  highlightedTasks.sort((a, b) => {
    const priorityDiff = priorityOrder[a.item.priority] - priorityOrder[b.item.priority];
    if (priorityDiff !== 0) return priorityDiff;
    return a.item.name.localeCompare(b.item.name);
  });

  return highlightedTasks;
};

const HighlightedTasks: React.FC<HighlightedTasksProps> = ({ tree }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const highlightedTasks = getHighlightedTasks(tree);

  // Don't render if no highlighted tasks
  if (highlightedTasks.length === 0) {
    return null;
  }

  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };

  const formatPath = (pathNames: string[]): string => {
    // Skip the root "Everything" node and show the path
    const path = pathNames.slice(1);
    if (path.length <= 1) {
      return path[0] || '';
    }

    // Show parent context for better understanding
    const parentPath = path.slice(0, -1).join(' › ');
    const taskName = path[path.length - 1];

    return `${parentPath} › ${taskName}`;
  };

  const getStatusIcon = (item: TodoItem): string => {
    if (item.done) return '✅';
    if (item.blocked) return '⏸️';
    return '⭕';
  };

  const getStatusTitle = (item: TodoItem): string => {
    if (item.done) return 'Completed';
    if (item.blocked) return 'Blocked';
    return 'Pending';
  };

  return (
    <div className={`highlighted-tasks ${isCollapsed ? 'collapsed' : ''}`}>
      <h4 className="highlighted-tasks-title" onClick={toggleCollapsed}>
        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
          {isCollapsed ? '▶' : '▼'}
        </span>
        🌟 Highlighted Tasks
        <span className="highlighted-tasks-count"> ({highlightedTasks.length})</span>
      </h4>

      {!isCollapsed && (
        <div className="highlighted-tasks-list">
          {highlightedTasks.map((task: HighlightedTask) => (
            <div key={task.id} className="highlighted-task-item">
              <div className="highlighted-task-content">
                <div className="highlighted-task-header">
                  <span
                    className="highlighted-task-status"
                    title={getStatusTitle(task.item)}
                  >
                    {getStatusIcon(task.item)}
                  </span>

                  <span
                    className="highlighted-task-priority"
                    style={{ color: getPriorityColor(task.item.priority) }}
                    title={`Priority: ${task.item.priority}`}
                  >
                    {getPriorityIcon(task.item.priority)}
                  </span>

                  <span className="highlighted-task-name">
                    {task.item.name}
                  </span>
                </div>

                {task.pathNames.length > 1 && (
                  <div className="highlighted-task-path">
                    {formatPath(task.pathNames)}
                  </div>
                )}

                {task.item.description && (
                  <div className="highlighted-task-description">
                    {task.item.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HighlightedTasks;
